/**
 * Shared humanizers + tones for work-order enums (urgency, status, category,
 * source, next-step lane, vendor-job status). The Work Orders list + card use
 * this so raw enums like PLUMBING / NORMAL / VENDOR_ASSIGNED read as clean
 * labels. WorkOrderDetailPage still has its own (older) maps — it can adopt
 * this next; the status labels here are kept in sync with its overrides.
 *
 * i18n (spec 91 R3): these are plain functions called both inside and OUTSIDE
 * React render (list transforms, filter options), so the label text is resolved
 * via the i18n SINGLETON — `i18n.t('common:…', { defaultValue })` — not the
 * useTranslation hook. `defaultValue` is byte-identical to the historical
 * English, so `en` output is unchanged and an unknown/missing key still yields
 * the humanized English. Tone/color logic is untouched — only display strings
 * become translated.
 */

import i18n from '../i18n';
import type { ChipTone } from '../components/StatusChip';

/**
 * THE work order's display identifier — `WO-257`, the one the WO record itself
 * prints and the only one a human may be asked to cross-reference.
 *
 * ★ IT IS `orderNumber`, NEVER `shortCode`. `WorkOrder.shortCode` is a RANDOM
 *   4-DIGIT EMERGENCY-SMS ACK CODE minted for every work order — its schema
 *   comment says so: "included in emergency SMS so the recipient can reply
 *   'ACK 4823' to acknowledge from a locked phone". It is not an identifier
 *   anyone reads a work order by, and it bears no relation to the WO number.
 *
 *   Surfaces that reached for `shortCode ?? orderNumber` printed `WO-9009` for
 *   the work order the detail page calls `WO-257` — two unrelated numbering
 *   systems wearing the same `WO-` prefix, so nothing could be read across.
 *   The accounting register was fixed for exactly this on 2026-08-05; the same
 *   mix-up survived in the make-ready folder and eight other places.
 *
 * There is no `shortCode` fallback because there is nothing to fall back FROM:
 * `orderNumber` is `Int @unique @default(autoincrement())` — non-null on every
 * row. Callers that accept a nullable number get `null` only for a missing/
 * unloaded work order, and every one of them already falls back to the title.
 */
export function woDisplayCode(
  wo: { orderNumber?: number | null } | null | undefined,
): string | null {
  const n = wo?.orderNumber;
  return n === null || n === undefined ? null : `WO-${n}`;
}

export interface UrgencyTone {
  label: string;
  color: string;
  bg: string;
  border: string;
}

export const URGENCY: Record<string, UrgencyTone> = {
  EMERGENCY: { label: 'Emergency', color: '#cf1322', bg: '#fff1f0', border: '#ffccc7' },
  URGENT:    { label: 'Urgent',    color: '#d4640e', bg: '#fff4e6', border: '#ffd591' },
  NORMAL:    { label: 'Normal',    color: '#1677ff', bg: '#e6f0ff', border: '#bfdcff' },
  ROUTINE:   { label: 'Normal',    color: '#1677ff', bg: '#e6f0ff', border: '#bfdcff' },
  LOW:       { label: 'Low',       color: '#4d5562', bg: '#f3f4f6', border: '#e2e4ea' },
};

export const urgencyTone = (u?: string | null): UrgencyTone => {
  const base = URGENCY[u ?? ''] ?? URGENCY.NORMAL;
  // Translate only the display label; color/bg/border tone is untouched.
  const key = URGENCY[u ?? ''] ? (u as string) : 'NORMAL';
  return { ...base, label: i18n.t(`common:woUrgency.${key}`, { defaultValue: base.label }) };
};

/**
 * Urgency → shared sig-chip tone (spec 118), by severity: Emergency = `danger`,
 * Urgent = `warn`, Normal = `info`, Low = `neutral`. Mirrors the
 * WorkOrderTemplatesPage urgency mapping so every urgency pill matches.
 */
const URGENCY_CHIP_TONE: Record<string, ChipTone> = {
  EMERGENCY: 'danger',
  URGENT: 'warn',
  NORMAL: 'info',
  ROUTINE: 'info',
  LOW: 'neutral',
};

export const urgencyChipTone = (u?: string | null): ChipTone => URGENCY_CHIP_TONE[u ?? ''] ?? 'info';

const STATUS_LABEL: Record<string, string> = {
  SUBMITTED: 'Submitted',
  TRIAGED: 'Triaged',
  ASSIGNED: 'Assigned',
  DISPATCH_DECISION: 'Dispatch decision',
  ESTIMATE_REQUESTED: 'Estimate requested',
  VENDOR_ASSIGNED: 'Vendor assigned',
  SCHEDULE_PROPOSED: 'Schedule proposed',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  INVOICE_PENDING: 'Awaiting vendor invoice',
  INVOICE_RECEIVED: 'Invoice approval pending',
  VERIFIED_CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const statusLabel = (s?: string | null): string => {
  const en = STATUS_LABEL[s ?? ''];
  // Known enum → translated (defaultValue = today's English). Unknown → humanized.
  return en !== undefined
    ? i18n.t(`common:woStatus.${s}`, { defaultValue: en })
    : (s ?? '').replace(/_/g, ' ');
};

/** Calm tonal set — grey / blue / orange / green, not a different color per status. */
export type StatusTone = 'blue' | 'orange' | 'green' | 'grey';

const STATUS_TONE: Record<string, StatusTone> = {
  SUBMITTED: 'blue', TRIAGED: 'blue', ASSIGNED: 'blue', DISPATCH_DECISION: 'blue',
  ESTIMATE_REQUESTED: 'blue', VENDOR_ASSIGNED: 'blue',
  SCHEDULE_PROPOSED: 'orange', SCHEDULED: 'orange', IN_PROGRESS: 'orange',
  INVOICE_PENDING: 'orange', INVOICE_RECEIVED: 'orange',
  COMPLETED: 'green', VERIFIED_CLOSED: 'green',
  CANCELLED: 'grey',
};

export const statusTone = (s?: string | null): StatusTone => STATUS_TONE[s ?? ''] ?? 'blue';

/**
 * WorkOrder status → shared sig-chip tone (spec 118), picked by MEANING so the
 * WO list card + WO detail render the SAME calm pill every other list uses.
 * Distinct from `statusTone` (the older 4-colour tonal set): here "In progress"
 * and "Vendor assigned — awaiting response" are `progress` (teal, active), the
 * awaiting-a-human states are `warn` (amber), a finished job is `ok`, and a
 * closed/cancelled one is inert `neutral`. Unknown → `neutral`.
 */
const STATUS_CHIP_TONE: Record<string, ChipTone> = {
  SUBMITTED: 'info', TRIAGED: 'info', ASSIGNED: 'info', DISPATCH_DECISION: 'info',
  ESTIMATE_REQUESTED: 'warn',        // awaiting a vendor bid
  VENDOR_ASSIGNED: 'progress',       // vendor assigned — awaiting their response
  SCHEDULE_PROPOSED: 'warn',         // awaiting schedule confirmation
  SCHEDULED: 'info',                 // upcoming booking
  IN_PROGRESS: 'progress',           // active / running
  INVOICE_PENDING: 'warn', INVOICE_RECEIVED: 'warn',
  COMPLETED: 'ok',
  VERIFIED_CLOSED: 'neutral',        // "Closed" — inert
  CANCELLED: 'neutral',
};

export const statusChipTone = (s?: string | null): ChipTone => STATUS_CHIP_TONE[s ?? ''] ?? 'neutral';

/**
 * The i18n key for a next-step headline — the ONE place that decides it.
 *
 * `stateKey` alone is not enough, because four rungs of the engine emit TWO
 * different English strings under a SINGLE key. Where web also has a translation
 * for that key, the translation WINS over the server's English and flattens the
 * two meanings into one — and `defaultValue` cannot save it, because the key
 * exists. That is how a CANCELLED work order came to read "Closed" (ES
 * "Cerrado") on the list card, beside a filter the PM had just set to Cancelled
 * (PO, 2026-08-21).
 *
 * Two of the four bite on web today:
 *   CLOSED                   → "Cancelled" | "Closed"
 *   NEEDS_DISPATCH_DECISION  → "Vendor declined — reassign" | "Needs dispatch decision"
 * The other two (`INVOICE`, `INTERNAL_OWNER_WORKING`) have no web translation at
 * all, so they fall through to the server's English — untranslated, but never
 * WRONG. They are listed here so the next person adding those keys sees that
 * doing so naively would create a third and fourth instance of this bug.
 *
 * The server's own English picks the variant, exactly as mobile's
 * `woNextStepLabel` does (`wo_next_step_labels.dart`) — matched on a lowercased
 * prefix so wording tweaks upstream degrade to the base translation rather than
 * to English.
 */
export function nextStepLabelKey(stateKey?: string | null, serverLabel?: string | null): string {
  const key = stateKey ?? '';
  const en = (serverLabel ?? '').trim().toLowerCase();
  if (key === 'CLOSED' && en.startsWith('cancelled')) return 'nextStep.label.CLOSED_CANCELLED';
  if (key === 'NEEDS_DISPATCH_DECISION' && en.startsWith('vendor declined')) {
    return 'nextStep.label.NEEDS_DISPATCH_DECISION_DECLINED';
  }
  return `nextStep.label.${key}`;
}

/**
 * The same rule for the "pending on" line, which uses the identical
 * `nextStep.pendingOn.<stateKey>` lookup and therefore flattens the same way.
 *
 * Only `NEEDS_DISPATCH_DECISION` bites here: the engine says "You — assign a
 * DIFFERENT vendor" once a vendor has declined, and web's single translation
 * says "dispatch a vendor or handle internally" for both — telling a PM to start
 * a dispatch they already started. (`INVOICE` varies too but has no web
 * translation, so it falls through to the server's English: untranslated, not
 * wrong. `CLOSED`'s pendingOn does not vary.)
 */
export function nextStepPendingOnKey(stateKey?: string | null, serverPendingOn?: string | null): string {
  const key = stateKey ?? '';
  const en = (serverPendingOn ?? '').trim().toLowerCase();
  if (key === 'NEEDS_DISPATCH_DECISION' && en.includes('different vendor')) {
    return 'nextStep.pendingOn.NEEDS_DISPATCH_DECISION_DECLINED';
  }
  return `nextStep.pendingOn.${key}`;
}

export const STATUS_TONE_STYLE: Record<StatusTone, { color: string; bg: string; dot: string }> = {
  blue:   { color: '#0958d9', bg: '#e6f0ff', dot: '#1677ff' },
  orange: { color: '#d4640e', bg: '#fff4e6', dot: '#d4640e' },
  green:  { color: '#389e0d', bg: '#f6ffed', dot: '#52c41a' },
  // 'grey' is only ever the CANCELLED tone. The old washed grey read as
  // "disabled"; a legible muted rose reads clearly as "cancelled / void"
  // (distinct from the pure-red EMERGENCY urgency pill by label + position).
  grey:   { color: '#9f1239', bg: '#ffe6ea', dot: '#e11d48' },
};

const CATEGORY_LABEL: Record<string, string> = {
  PLUMBING: 'Plumbing',
  ELECTRICAL: 'Electrical',
  HVAC: 'HVAC',
  APPLIANCE: 'Appliance',
  PEST_CONTROL: 'Pest control',
  LOCKSMITH: 'Locksmith',
  GENERAL_MAINTENANCE: 'General maintenance',
  PAINTING: 'Painting',
  FLOORING: 'Flooring',
  CLEANING: 'Cleaning',
  STRUCTURAL: 'Structural',
  LANDSCAPING: 'Landscaping',
  OTHER: 'Other',
};

/** 13 canonical categories, in dispatch order — for filter dropdowns. */
export const WO_CATEGORIES = Object.keys(CATEGORY_LABEL);

export const categoryLabel = (c?: string | null): string => {
  if (!c) return i18n.t('common:woCategory.OTHER', { defaultValue: 'Other' });
  const en = CATEGORY_LABEL[c];
  // Known enum → translated (defaultValue = today's English). Unknown → humanized.
  return en !== undefined
    ? i18n.t(`common:woCategory.${c}`, { defaultValue: en })
    : c.charAt(0).toUpperCase() + c.slice(1).toLowerCase().replace(/_/g, ' ');
};

export interface CategoryTone {
  color: string;
  bg: string;
  border: string;
}

/**
 * A stable, readable color per work-order category (customer feedback: the
 * category chip used to be one washed grey for everything). Each hue is a dark,
 * saturated *text* color on a soft tint — high-contrast and easy to read, never
 * bright or washed out. Violet/purple is deliberately avoided (reserved for the
 * brand + AI accents). Unknown categories fall back to the neutral OTHER tone.
 */
const CATEGORY_TONE: Record<string, CategoryTone> = {
  PLUMBING:            { color: '#1461c7', bg: '#e9f1fe', border: '#cfe0fb' },
  ELECTRICAL:          { color: '#9a6206', bg: '#fdf0d5', border: '#f6dfa8' },
  HVAC:                { color: '#0e7490', bg: '#e0f4f9', border: '#bde6f0' },
  /* Indigo is this CATEGORY's own tone, not the brand — every entry here is a
     self-contained literal triple so the scale stays independent of the accent. */
  APPLIANCE:           { color: '#3312c4', bg: '#eaebfb', border: '#d3d5f6' },
  PEST_CONTROL:        { color: '#5a6b12', bg: '#f2f6da', border: '#e0e8ad' },
  LOCKSMITH:           { color: '#8a5a24', bg: '#f6ecdd', border: '#ecd9bd' },
  GENERAL_MAINTENANCE: { color: '#3a5a8c', bg: '#eaf0f8', border: '#cedeef' },
  PAINTING:            { color: '#a83289', bg: '#fbe8f4', border: '#f4cfe6' },
  FLOORING:            { color: '#b25411', bg: '#ffedda', border: '#fcd7b0' },
  CLEANING:            { color: '#0f766e', bg: '#ddf2ef', border: '#b6e4dd' },
  STRUCTURAL:          { color: '#a63c1e', bg: '#fce9e1', border: '#f5cdba' },
  LANDSCAPING:         { color: '#2f7d3b', bg: '#e4f4e5', border: '#c2e6c6' },
  OTHER:               { color: '#57606f', bg: '#eff1f4', border: '#dde1e7' },
};

const CATEGORY_TONE_DEFAULT: CategoryTone = CATEGORY_TONE.OTHER;

export const categoryTone = (c?: string | null): CategoryTone =>
  (c && CATEGORY_TONE[c]) || CATEGORY_TONE_DEFAULT;

const SOURCE_LABEL: Record<string, string> = {
  IN_APP_CHAT: 'In-app chat',
  SMS: 'SMS',
  PHONE: 'Phone',
  EMAIL: 'Email',
  INTERCOM: 'Intercom',
  PM_PORTAL: 'PM portal',
};

export const sourceLabel = (s?: string | null): string => {
  if (!s) return '';
  const en = SOURCE_LABEL[s];
  return en !== undefined
    ? i18n.t(`common:woSource.${s}`, { defaultValue: en })
    : s.replace(/_/g, ' ');
};

/**
 * VendorJobStatus → chip tone. The little vendor-status pill on the WO card used
 * to be one grey for every state; this colors it semantically (waiting = amber,
 * accepted/booked = blue/cyan, working = orange, done = green, declined = rose).
 */
export interface JobStatusTone {
  color: string;
  bg: string;
  border: string;
}

const JOB_STATUS_TONE: Record<string, JobStatusTone> = {
  PENDING:     { color: '#9a6206', bg: '#fdf1d6', border: '#f3dfab' },
  ACCEPTED:    { color: '#0958d9', bg: '#e6f0ff', border: '#c6dbff' },
  SCHEDULED:   { color: '#0e7490', bg: '#e0f4f9', border: '#bde6f0' },
  IN_PROGRESS: { color: '#c05e0d', bg: '#fff2e2', border: '#ffd8ab' },
  COMPLETED:   { color: '#389e0d', bg: '#eaf7e1', border: '#c5eab0' },
  DECLINED:    { color: '#c2334d', bg: '#ffe6ea', border: '#ffccd4' },
  CANCELLED:   { color: '#57606f', bg: '#eef0f3', border: '#dde1e7' },
};

const JOB_STATUS_TONE_DEFAULT: JobStatusTone = JOB_STATUS_TONE.CANCELLED;

export const jobStatusTone = (s?: string | null): JobStatusTone =>
  (s && JOB_STATUS_TONE[s]) || JOB_STATUS_TONE_DEFAULT;

/** VendorJobStatus → human label ("On my way", "Need parts", "Arrived"). */
export const jobStatusLabel = (s?: string | null): string => {
  if (!s) return '';
  // Humanized English is the fallback → en stays byte-identical, unknowns humanize.
  const en = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
  return i18n.t(`common:woJobStatus.${s}`, { defaultValue: en });
};

/** next-step lane → AntD Tag color. Violet stays reserved for AI accents. */
export const nextStepLaneColor = (lane?: string | null): string => {
  switch (lane) {
    case 'INTERNAL': return 'blue';
    case 'VENDOR': return 'geekblue';
    case 'RESIDENT': return 'orange';
    case 'DONE': return 'green';
    default: return 'default';
  }
};
